import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useViewContext from '@modules/order/hooks/use-view-context'
import { QUANTITIES_AND_BOOK } from '@modules/order/stores/view/List/ListViewTableService'
import React, { FC, memo, useMemo } from 'react'
import { useObserver } from 'mobx-react-lite'
import isNumber from 'lodash/isNumber'
import useOrderContext from '@modules/order/hooks/use-order-context'
import formatNumberByLocale from '@utils/format-number-by-locale'
import formatCurrencyValueByLocale from '@utils/format-currency-value-by-locale'
import { QuantityType } from '@components/UI-Components/QuantityType/constants/QuantityType'
import HierarchyCell from '@modules/order/components/CellTemplates/components/HierarchyCell/HierarchyCell'
import FieldSkeleton from '@api/order/components/CellLoaders/FieldSkeleton'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import { CentricTypography as Typography } from '@core/component/CentricTypography'
import { CATEGORY_INTERNAL_SEPERATOR } from '@modules/order/constants/uiConstants'
import { getKPICurrency } from '@modules/order/utils/get-kpi-currency'
import { nav } from '@stores'

const HierarchyQuantityValue: FC<CellItem> = ({ rowData, column }) =>
  useObserver(function useHook() {
    const { uiStore } = useViewContext()
    const {
      uiStore: {
        dataStore: { summary },
        quantityTypeStore: { type }
      }
    } = useOrderContext()
    const {
      isValueSelected,
      getOrderHierarchyKPIs,
      getDoorOrderHierarchyKPIs,
      dataStore: { heirarchy, totalDoorKPIs }
    } = uiStore as EnhancedListUIStore<any>
    const { field } = column
    const isMainHeirarchy = !rowData?.name?.includes(CATEGORY_INTERNAL_SEPERATOR)
    const doorOrder =
      field === QUANTITIES_AND_BOOK
        ? getOrderHierarchyKPIs(rowData?.name, heirarchy)
        : getDoorOrderHierarchyKPIs(field, rowData?.name, totalDoorKPIs)

    const captureSize = useMemo(
      () => summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize,
      [summary]
    )
    const currency = getKPICurrency(nav.queryParams.orderType, summary)
    const totalByDraft = useMemo(
      () =>
        isValueSelected
          ? doorOrder?.totalDoorValue || 0
          : doorOrder?.totalDoorQuantity || 0,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [doorOrder, isValueSelected]
    )
    const totalBySize = useMemo(
      () =>
        isValueSelected
          ? doorOrder?.totalSizeValue || 0
          : doorOrder?.totalSizeQuantity || 0,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [doorOrder, isValueSelected]
    )

    /**
     * process currency-value string
     * show currency if switch is on Value
     * product qty type -> draft / size if captureSize enabled, else draft
     * size qty type -> size fields
     * door qty type -> draft fields
     */
    const qtyValueStr = useMemo(() => {
      let str = ''
      if (isNumber(totalByDraft) && isNumber(totalBySize)) {
        switch (type) {
          case QuantityType.Product:
            str = captureSize
              ? `${
                  isValueSelected
                    ? formatCurrencyValueByLocale(currency, totalByDraft)
                    : formatNumberByLocale(totalByDraft)
                } / ${
                  isValueSelected
                    ? formatCurrencyValueByLocale(currency, totalBySize)
                    : formatNumberByLocale(totalBySize)
                }`
              : `${
                  isValueSelected
                    ? formatCurrencyValueByLocale(currency, totalByDraft)
                    : formatNumberByLocale(totalByDraft)
                }`
            break
          case QuantityType.Draft:
            str = `${
              isValueSelected
                ? formatCurrencyValueByLocale(currency, totalByDraft)
                : formatNumberByLocale(totalByDraft)
            }`
            break
          case QuantityType.DraftWHL:
            str = `${
              isValueSelected
                ? formatCurrencyValueByLocale(currency, totalByDraft)
                : formatNumberByLocale(totalByDraft)
            }`
            break
          case QuantityType.Size:
            str = `${
              isValueSelected
                ? formatCurrencyValueByLocale(currency, totalBySize)
                : formatNumberByLocale(totalBySize)
            }`
            break
        }
      }
      return str
    }, [currency, totalByDraft, totalBySize, isValueSelected, type, captureSize])

    if (!rowData) {
      return (
        <HierarchyCell>
          <FieldSkeleton />
        </HierarchyCell>
      )
    }
    return (
      <HierarchyCell>
        <Typography variant={isMainHeirarchy ? 'subtitle1' : 'caption'}>
          {qtyValueStr}
        </Typography>
      </HierarchyCell>
    )
  })

export default memo(HierarchyQuantityValue)
